import FeatureAppModel from './FeatureAppModel';
import { registry, dispatchQueue } from './globals';
/**
 * Register a Feature App instance
 * @param id Feature App identifier. Null for the root/page.
 * @param values Feature App metadata
 */
function register(id, values) {
    const app = new FeatureAppModel(values);
    registry.set(id, app);
    // go through the dispatch queue after this sync operation batch is completed
    setTimeout(() => {
        flushDispatchQueue();
    }, 0);
    return app;
}
/**
 * Find an instantiated Feature App from the registry.
 * @param identifier ID or Route of the app to find
 */
function find(identifier) {
    if (registry.has(identifier)) {
        return registry.get(identifier);
    }
    for (const [id] of registry) {
        // Get FA instance
        const app = registry.get(id);
        if (id === identifier ||
            app?.id === identifier ||
            app?.name === identifier ||
            Object.keys(app?.meta || {}).some((metaKey) => app.meta[metaKey] === identifier)) {
            return app;
        }
    }
    return undefined;
}
/**
 * Find All apps that match the query
 * @param query ID or Route of the app to find
 */
function findAll(query) {
    const results = Array.from(registry, ([id, app]) => 
    // check if id matches
    query.id === id ||
        query.id === app.id ||
        query.id === app.name ||
        // check if any property matches one on one
        Object.keys(query).some((key) => app[key] === query[key]) ||
        // check if any meta property matches one on one
        Object.keys(query).some((key) => app.meta[key] === query[key]) ||
        // tags are matching any of the tag
        app.tags?.some?.((appTag) => query?.tags?.includes?.(appTag)) ||
        // tags are matching the only provided tag in query
        (typeof query.tag === 'string' && app.tags?.includes?.(String(query.tag)))
        ? app
        : false).filter(Boolean);
    return results;
}
/**
 * Find all Feature Apps with a certain tag
 * @param tag Tag
 */
function findTag(tag) {
    if (tag === '*') {
        // wildcard for all entries
        return Array.from(registry.values());
    }
    return Array.from(registry)
        .filter(([, app]) => app.tags?.includes(tag))
        .map(([, app]) => app);
}
function doc() {
    const docFeatureAppModel = find('root');
    return docFeatureAppModel;
}
function docData() {
    const docFeatureAppModel = doc();
    return docFeatureAppModel?.data || {};
}
/**
 * Dispatcher emits actions and payloads to a feature app that matches the identifier
 * Example
 *    dispatch<string>('root', 'changeTitle', title).then(([res]) =>
 *      console.log('action responded with', res?.response),
 *    );
 *
 *
 * @param identifier
 * @param actionName
 * @param args
 */
function dispatch(identifier, actionName, ...args) {
    const dispatchPromise = new Promise((resolve, reject) => {
        dispatchQueue.set([resolve, reject], [identifier, actionName, ...args]);
    });
    flushDispatchQueue();
    return dispatchPromise;
}
/**
 * Smart find by a string that can match tags or query or string
 * @param identifier
 */
function findAny(identifier) {
    let instances = [];
    if (typeof identifier === 'string') {
        // searching for IDs
        instances = [find(identifier)].filter(Boolean);
        if (!instances.length) {
            // If we haven't found anything so far, let's try to search for tags
            instances = findTag(identifier);
        }
    }
    else if (typeof identifier === 'object') {
        // search by all query fields if Query object is supplied
        instances = findAll(identifier);
    }
    if (!instances?.length) {
        return [];
    }
    // Dispatch to all found instances
    const result = instances.filter(Boolean);
    return result;
}
function flushDispatchQueue() {
    Array.from(dispatchQueue, ([key, [identifier, actionName, ...args]]) => {
        const [resolve, reject] = key;
        const instances = findAny(identifier);
        if (!instances.length) {
            return reject(`no instanced matching ${identifier} found`);
        }
        const results = instances.map((instance) => ({
            response: instance.dispatch(actionName, ...args),
            app: instance,
        }));
        dispatchQueue.delete(key);
        resolve(results);
    });
}
export { register, registry, find, findTag, findAll, doc, docData, dispatch, findAny, };
if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    // expose variables for development purposes
    globalThis.registry = registry;
    globalThis.dispatch = dispatch;
    globalThis.flushDispatchQueue = flushDispatchQueue;
    globalThis.dispatchQueue = dispatchQueue;
}
